import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Chip,
  Divider,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Rating,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  useTheme,
  useMediaQuery,
  Collapse,
  Slide,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useLocation, useNavigate } from "react-router-dom";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import axios from "axios";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import DocumentViewerDialog from "./DocumentViewerDialog";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PastTripCard from "./PastTripCard";
import HouseIcon from "@mui/icons-material/House";

const PRIMARY = "#7B6ED6";
const BG = "#F6F7FB";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

// Initial Customer State
const initialCustomerState = {
  partyCode: "",
  waNumber: "",
  mobile: "",
  relatedMobiles: [],
  name: "",
  email: "",
  dob: "",
  note: "",
  customerType: "",
  stage: "Customer",
  pickupAddress: "",
  pickupLocations: [],
  cust_preferences: [], // ← was: preferences
  businessInfo: {
    companyName: "",
    companyEmail: "",
    gstNumber: "",
  },
  documents: [],
  cust_type: "",
  pickupPlace: "",
  trips: [],
};

// Stage options constant
const STAGE_OPTIONS = [
  "Hot Leads",
  "Warm Leads",
  "Cold Leads",
  "Contacted",
  "Demo Scheduled",
  "Demo Completed",
  "Proposal / Quotation Sent",
  "Negotiation",
  "Decision Pending",
  "Customer",
  "Lost",
  "On Hold",
  "Future",
];

const CATEGORY_OPTIONS = ["A", "B", "C", "D"];

const SORT_OPTIONS = [
  { label: "A-Z", value: "ASC" },
  { label: "Z-A", value: "DESC" },
  { label: "Oldest First", value: "OLDEST" },
  { label: "Newest First", value: "NEWEST" },
];

// Shared glass-chip style
const glassChipSx = (isSelected) => ({
  fontWeight: isSelected ? 600 : 500,
  borderRadius: "10px",
  color: isSelected ? "#fff" : "rgba(255,255,255,0.9)",
  backgroundColor: isSelected ? PRIMARY : "rgba(255,255,255,0.12)",
  border: isSelected
    ? "1px solid rgba(255,255,255,0.5)"
    : "1px solid rgba(255,255,255,0.2)",
  boxShadow: isSelected ? "0 4px 14px rgba(0,0,0,0.25)" : "none",
  backdropFilter: "blur(6px)",
  transition: "all 0.25s ease",
  "&:hover": {
    backgroundColor: isSelected ? PRIMARY : "rgba(255,255,255,0.25)",
  },
});

// Bold white section label
const FilterLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      color: "#fff",
      fontWeight: 700,
      fontSize: "0.75rem",
      display: "block",
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

const CustomerMaster = () => {
  // Responsive Layout Parameters
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));

  const topRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  // Who and where. location.state carries these when the screen is opened from
  // inside the app (the booking flow passes isCreateBooking through it); on a
  // direct load or an F5 there is no router state, so fall back to the session
  // the way DriverMaster does. Reading state unguarded white-screened the route,
  // and destructuring a null state threw before any of it could render.
  const { userInfo, currentStoreInfo } = useContext(AutoTaskAuthContext) || {};
  const userID = location.state?.user_id ?? userInfo?.id;
  const userStoreID =
    location.state?.store_id ?? currentStoreInfo?.id ?? userInfo?.store_id;
  const { isCreateBooking } = location?.state || {};

  const [searchText, setSearchText] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(initialCustomerState);
  const [customers, setCustomers] = useState([]);
  const [isNewCustomer, setIsNewCustomer] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const [isWaManuallyEdited, setIsWaManuallyEdited] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);

  const [allPreferences, setAllPreferences] = useState([]);
  const [preferenceSearch, setPreferenceSearch] = useState("");

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [showCompanyDetails, setShowCompanyDetails] = useState(false);
  const [duplicateFields, setDuplicateFields] = useState({
    mobile: null,
    waNumber: null,
    relatedMobiles: {},
  });
  const [isMobileChecking, setIsMobileChecking] = useState(false);
  const mobileCheckTimeoutRef = useRef(null);

  const [fieldErrors, setFieldErrors] = useState({
    name: false,
    mobile: false,
    pickupAddress: false,
  });

  const [sortOrder, setSortOrder] = useState("ASC");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [allCustomerTypes, setAllCustomerTypes] = useState([]);
  const [customerTypeSearch, setCustomerTypeSearch] = useState("");

  const [selectedTypeFilter, setSelectedTypeFilter] = useState(null);

  // Filter states
  const [allPlaces, setAllPlaces] = useState([]);
  const [selectedPlaceFilter, setSelectedPlaceFilter] = useState(null);
  const [selectedPreferenceFilter, setSelectedPreferenceFilter] =
    useState(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(null);
  const [selectedStageFilter, setSelectedStageFilter] = useState(null);
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCustomers, setTotalCustomers] = useState(0);

  // Search debounce
  const searchTimeoutRef = useRef(null);
  const listRef = useRef(null);
  const partyCodeTimeoutRef = useRef(null);
  const [partyCodeDuplicate, setPartyCodeDuplicate] = useState(null);
  const [searchMode, setSearchMode] = useState("wild");

  const isEditMode = !!selectedCustomerId && !isNewCustomer;

  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const closeSnackbar = () =>
    setSnackBarConfig((prev) => ({ ...prev, open: false }));

  // WhatsApp number same as mobile checker
  useEffect(() => {
    if (!isWaManuallyEdited) {
      setCustomerDetail((prev) => ({
        ...prev,
        waNumber: prev.mobile,
      }));
    }
  }, [customerDetail.mobile, isWaManuallyEdited]);

  const handleAddNewCustomer = () => {
    setSelectedCustomerId(null);
    setCustomerDetail(initialCustomerState);
    setIsNewCustomer(true);
    setIsWaManuallyEdited(false);
    setDuplicateFields({ mobile: null, waNumber: null, relatedMobiles: {} });
    setPartyCodeDuplicate(null);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    setFieldErrors({ name: false, mobile: false, pickupAddress: false });
    // setSelectedCustomerTrips([]);
  };

  const handleChange = (field, value) => {
    setCustomerDetail((p) => ({ ...p, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  // Check for Duplicate Party Code
  const checkPartyCodeDuplicate = useCallback(
    async (code) => {
      if (!code || !code.trim()) {
        setPartyCodeDuplicate(null);
        return;
      }
      if (partyCodeTimeoutRef.current)
        clearTimeout(partyCodeTimeoutRef.current);
      partyCodeTimeoutRef.current = setTimeout(async () => {
        try {
          const res = await axios.get(
            `${API_BASE_URL}/api/travel/customers/store/${userStoreID}/party/${code.trim()}`,
          );
          if (res.data.success) setPartyCodeDuplicate(res.data.data);
          else setPartyCodeDuplicate(null);
        } catch {
          setPartyCodeDuplicate(null);
        }
      }, 500);
    },
    [userStoreID],
  );

  // Check for Duplicate Mobiles
  const checkNumberDuplicate = useCallback(
    async (fieldKey, number, relatedIndex = null) => {
      const clean = number?.toString().replace(/\D/g, "") || "";
      if (clean.length < 6) {
        setDuplicateFields((prev) => {
          if (fieldKey === "relatedMobiles") {
            return {
              ...prev,
              relatedMobiles: { ...prev.relatedMobiles, [relatedIndex]: null },
            };
          }
          return { ...prev, [fieldKey]: null };
        });
        return;
      }
      if (mobileCheckTimeoutRef.current)
        clearTimeout(mobileCheckTimeoutRef.current);
      mobileCheckTimeoutRef.current = setTimeout(async () => {
        try {
          setIsMobileChecking(true);
          const params = { numbers: clean };
          if (selectedCustomerId)
            params.exclude_customer_id = selectedCustomerId;
          const res = await axios.get(
            `${API_BASE_URL}/api/travel/customers/check-duplicate/${userStoreID}`,
            { params },
          );
          if (res.data.success) {
            const result = res.data.data;
            setDuplicateFields((prev) => {
              if (fieldKey === "relatedMobiles") {
                return {
                  ...prev,
                  relatedMobiles: {
                    ...prev.relatedMobiles,
                    [relatedIndex]: result.isDuplicate
                      ? result.existingCustomer
                      : null,
                  },
                };
              }
              return {
                ...prev,
                [fieldKey]: result.isDuplicate ? result.existingCustomer : null,
              };
            });
          }
        } catch {
          // silently fail
        } finally {
          setIsMobileChecking(false);
        }
      }, 600);
    },
    [userStoreID, selectedCustomerId],
  );

  const handleBusinessChange = (field, value) =>
    setCustomerDetail((p) => ({
      ...p,
      businessInfo: { ...p.businessInfo, [field]: value },
    }));

  const toggleChip = (field, value) =>
    setCustomerDetail((p) => ({ ...p, [field]: value }));

  // Toggle a preference on/off for the current customer
  const togglePreference = (preference) => {
    setCustomerDetail((prev) => {
      const exists = prev.cust_preferences.some(
        (p) =>
          (p.id && p.id === preference.id) ||
          p.name.toLowerCase() === preference.name.toLowerCase(),
      );

      if (exists) {
        return {
          ...prev,
          cust_preferences: prev.cust_preferences.filter(
            (p) =>
              !(
                (p.id && p.id === preference.id) ||
                p.name.toLowerCase() === preference.name.toLowerCase()
              ),
          ),
        };
      }

      return {
        ...prev,
        cust_preferences: [...prev.cust_preferences, preference],
      };
    });
  };

  const updateDocument = (i, field, value) => {
    const docs = [...customerDetail.documents];
    docs[i][field] = value;
    setCustomerDetail((p) => ({ ...p, documents: docs }));
  };

  const handleFileUpload = (i, e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateDocument(i, "file", reader.result);
        updateDocument(i, "fileName", file.name);
      };
      reader.readAsDataURL(file);
    }
  };

  const addDocument = () =>
    setCustomerDetail((p) => ({
      ...p,
      documents: [
        ...p.documents,
        { name: "", number: "", file: null, fileName: "", renewalDate: "" },
      ],
    }));

  const deleteDocument = (i) =>
    setCustomerDetail((p) => ({
      ...p,
      documents: p.documents.filter((_, index) => index !== i),
    }));

  const handleCancel = () => {
    setCustomerDetail(initialCustomerState);
    setSelectedCustomerId(null);
    setIsNewCustomer(true);
    setShowCompanyDetails(false);
    setDuplicateFields({ mobile: null, waNumber: null, relatedMobiles: {} });
    setPartyCodeDuplicate(null);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    setFieldErrors({ name: false, mobile: false, pickupAddress: false });
  };

  // Create a new preference and immediately select it
  const createAndSelectPreference = () => {
    const newPreferenceName = preferenceSearch.trim();
    if (!newPreferenceName) return;

    const newPreference = {
      id: null,
      name: newPreferenceName,
      store_id: userStoreID,
    };

    setAllPreferences((prev) => [...prev, newPreference]);
    setCustomerDetail((prev) => ({
      ...prev,
      cust_preferences: [...prev.cust_preferences, newPreference],
    }));
    setPreferenceSearch("");
  };

  /* ==================== RELATED MOBILES ==================== */
  const addRelatedMobile = () =>
    setCustomerDetail((customer) => ({
      ...customer,
      relatedMobiles: [
        ...customer.relatedMobiles,
        { relation: "", number: "" },
      ],
    }));

  const updateRelatedMobile = (i, field, value) => {
    const relatedMobiles = [...customerDetail.relatedMobiles];
    relatedMobiles[i][field] = value;
    setCustomerDetail((customer) => ({ ...customer, relatedMobiles }));
    if (field === "number") checkNumberDuplicate("relatedMobiles", value, i);
  };

  const deleteRelatedMobile = (i) => {
    setCustomerDetail((customer) => ({
      ...customer,
      relatedMobiles: customer.relatedMobiles.filter((_, index) => index !== i),
    }));
    setDuplicateFields((prev) => {
      const updated = { ...prev.relatedMobiles };
      delete updated[i];
      const reindexed = {};
      Object.entries(updated).forEach(([key, val]) => {
        const k = parseInt(key);
        reindexed[k > i ? k - 1 : k] = val;
      });
      return { ...prev, relatedMobiles: reindexed };
    });
  };

  /* ==================== PICK-UP LOCATIONS ==================== */
  const addPickupLocation = () =>
    setCustomerDetail((prev) => ({
      ...prev,
      pickupLocations: [...prev.pickupLocations, { label: "", link: "" }],
    }));

  const updatePickupLocation = (index, field, value) => {
    const locations = [...customerDetail.pickupLocations];
    locations[index][field] = value;
    setCustomerDetail((prev) => ({ ...prev, pickupLocations: locations }));
  };

  const deletePickupLocation = (index) =>
    setCustomerDetail((prev) => ({
      ...prev,
      pickupLocations: prev.pickupLocations.filter((_, i) => i !== index),
    }));

  /* ==================== API CALLS ==================== */

  // Fetch all customer preferences (was: customerGroupsFetcher)
  const customerPreferencesFetcher = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/customers/customer-preferences/${userStoreID}`,
      );
      if (!res.data.success) throw res.data;
      setAllPreferences(res.data?.data ?? []);
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: error.statusMessage ?? "Failed to load customer preferences",
        severity: "error",
      });
    }
  };

  const customerTypesFetcher = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/customers/customer-types/${userStoreID}`,
      );
      if (res.data.success) setAllCustomerTypes(res.data.data ?? []);
    } catch {
      console.error("Failed to load customer types");
    }
  };

  const placesFetcher = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/customers/pickup-places/${userStoreID}`,
      );
      if (res.data.success) setAllPlaces(res.data.data ?? []);
    } catch {
      console.error("Failed to load pickup places");
    }
  };

  useEffect(() => {
    customerPreferencesFetcher();
    customerTypesFetcher();
    placesFetcher();
  }, []);

  useEffect(() => {
    if (selectedPlaceFilter && !allPlaces.includes(selectedPlaceFilter)) {
      setSelectedPlaceFilter(null);
    }
  }, [allPlaces, selectedPlaceFilter]);

  // Get Customers with Pagination
  const customersFetcher = useCallback(
    async (pageNum = 1, isLoadMore = false, silent = false) => {
      try {
        if (!isLoadMore && !silent) setIsListLoading(true);
        else if (isLoadMore) setIsLoadingMore(true);

        const res = await axios.get(
          `${API_BASE_URL}/api/travel/customers/store/${userStoreID}`,
          {
            params: {
              page: pageNum,
              limit: 25,
              search: searchText.trim(),
              search_mode: searchMode,
              sort: sortOrder,
              cust_type: selectedTypeFilter,
              place: selectedPlaceFilter,
              preference: selectedPreferenceFilter,
              category: selectedCategoryFilter,
              stage: selectedStageFilter,
            },
          },
        );

        const customersFetcherData = res.data?.data ?? [];
        const pagination = res.data?.pagination;

        if (!res.data.success) throw res.data;

        if (isLoadMore)
          setCustomers((prev) => [...prev, ...customersFetcherData]);
        else setCustomers(customersFetcherData);

        setTotalCustomers(pagination?.total || 0);
        setHasMore(pagination?.hasNextPage || false);
        setPage(pageNum);
      } catch (error) {
        setSnackBarConfig({
          open: true,
          message: error.statusMessage ?? "Failed to load customers",
          severity: "error",
        });
      } finally {
        setIsListLoading(false);
        setIsLoadingMore(false);
      }
    },
    [
      userStoreID,
      searchText,
      searchMode,
      sortOrder,
      selectedTypeFilter,
      selectedPlaceFilter,
      selectedPreferenceFilter,
      selectedCategoryFilter,
      selectedStageFilter,
    ],
  );

  useEffect(() => {
    setPage(1);
    setCustomers([]);
    setHasMore(true);
    customersFetcher(1, false);
  }, [
    selectedTypeFilter,
    selectedPlaceFilter,
    selectedPreferenceFilter,
    selectedCategoryFilter,
    selectedStageFilter,
  ]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setCustomers([]);
      customersFetcher(1, false);
    }, 500);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchText, sortOrder, searchMode]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || isLoadingMore || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      customersFetcher(page + 1, true);
    }
  }, [page, hasMore, isLoadingMore, customersFetcher]);

  // Fetch ONE customer with all details
  const fetchOneCustomer = async (customerID, customerPhone) => {
    try {
      setIsListLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/customers/store/${userStoreID}/id/${customerID}`,
      );

      if (!res.data.success) throw res.data;

      const fc = res.data.data; // fc = fetched customer
      const business = fc.businessInfo || {};
      setShowCompanyDetails(
        !!(business.companyName || business.companyEmail || business.gstNumber),
      );

      setCustomerDetail({
        partyCode: fc.party_code || "",
        waNumber: fc.whatsapp?.toString() || "",
        mobile: fc.mobile?.toString() || "",
        relatedMobiles: (fc.relatedMobiles || []).map((r) => ({
          relation: r.relation,
          number: r.number,
        })),
        name: fc.name || "",
        email: fc.email || "",
        dob: fc.dob || "",
        note: fc.note || "",
        stage: fc.stage || "",
        customerType: fc.customerType || "",
        pickupAddress: fc.pickupAddress || "",
        pickupLocations: (fc.pickupLocations || []).map((loc) => ({
          label: loc.label || "",
          link: loc.link || "",
        })),
        // BE returns `cust_preferences` as array of { id, name, store_id }
        cust_preferences: fc.cust_preferences || [],
        businessInfo: {
          companyName: fc.businessInfo?.companyName || "",
          companyEmail: fc.businessInfo?.companyEmail || "",
          gstNumber: fc.businessInfo?.gstNumber || "",
        },
        documents: (fc.customer_documents || []).map((doc) => ({
          doc_id: doc.doc_id,
          name: doc.name,
          number: doc.number,
          file: doc.file,
          fileName: doc.fileName,
          renewalDate: doc.renewalDate || "",
        })),
        cust_type: fc.cust_type || "",
        pickupPlace: fc.pickupPlace || "",
        trips: fc.trips || [],
      });

      setDuplicateFields({ mobile: null, waNumber: null, relatedMobiles: {} });
      setIsWaManuallyEdited(true);
      setSelectedCustomerId(customerID);
      setIsNewCustomer(false);

      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);

      setSnackBarConfig({
        open: true,
        message: "Customer loaded successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("❌ Fetch Customer Error:", error);
      setSnackBarConfig({
        open: true,
        message: error?.statusMessage || "Failed to load customer data",
        severity: "error",
      });
    } finally {
      setIsListLoading(false);
    }
  };

  // Fetch customer by mobile number
  const handleFetchByMobile = async (mobile) => {
    const clean = mobile?.toString().replace(/\D/g, "") || "";
    if (clean.length < 6) return;

    try {
      setIsListLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/customers/store/${userStoreID}/phone/${clean}`,
      );

      if (!res.data.success) {
        setSnackBarConfig({
          open: true,
          message: "No customer found with this mobile number",
          severity: "warning",
        });
        return;
      }

      const fc = res.data.data;
      fetchOneCustomer(fc.customer_id);
    } catch {
      setSnackBarConfig({
        open: true,
        message: "Failed to fetch customer by mobile",
        severity: "error",
      });
    } finally {
      setIsListLoading(false);
    }
  };

  // Delete a customer
  const deleteCustomer = async (customerID) => {
    try {
      setIsListLoading(true);
      setIsSaving(true);
      const res = await axios.delete(
        `${API_BASE_URL}/api/travel/customers/${customerID}`,
        { data: { store_id: userStoreID } },
      );
      if (!res.data.success) throw res.data;
      setSelectedCustomer(null);
      setOpenDialog(false);
      handleCancel();
      setSnackBarConfig({
        open: true,
        message: res.data.statusMessage || "Customer deleted successfully!",
        severity: "success",
      });
      refreshCustomers();
      await placesFetcher();
    } catch (error) {
      console.error("❌ Delete Customer Error:", error);
      setSnackBarConfig({
        open: true,
        message: error?.statusMessage || "Failed to delete customer data",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Refresh the customers list
  const refreshCustomers = useCallback(
    async (silent = false) => {
      setPage(1);
      setHasMore(true);
      await Promise.all([customersFetcher(1, false, silent), placesFetcher()]);
    },
    [customersFetcher],
  );

  // Create / Update Customer
  const handleSave = async () => {
    // Mandatory field validation
    const errors = {
      name: !customerDetail.name.trim(),
      mobile: !customerDetail.mobile.trim(),
      pickupAddress: !customerDetail.pickupAddress.trim(),
    };
    setFieldErrors(errors);

    if (Object.values(errors).some(Boolean)) {
      setSnackBarConfig({
        open: true,
        message:
          "Please fill in all required fields: Name, Mobile, and Pick-up Address.",
        severity: "error",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (customerDetail.email && !emailRegex.test(customerDetail.email)) {
      setSnackBarConfig({
        open: true,
        message: "Invalid customer email format",
        severity: "error",
      });
      return;
    }
    if (
      customerDetail.businessInfo.companyEmail &&
      !emailRegex.test(customerDetail.businessInfo.companyEmail)
    ) {
      setSnackBarConfig({
        open: true,
        message: "Invalid company email format",
        severity: "error",
      });
      return;
    }

    const payload = {
      party_code: customerDetail.partyCode || null,
      store_id: userStoreID,
      created_by: userID,
      name: customerDetail.name,
      mobile: customerDetail.mobile,
      relatedMobiles: customerDetail.relatedMobiles.map((r) => ({
        relation: r.relation,
        number: r.number,
      })),
      whatsapp: customerDetail.waNumber,
      dob: customerDetail.dob || null,
      email: customerDetail.email || null,
      note: customerDetail.note || null,
      stage: customerDetail.stage,
      type: customerDetail.customerType,
      pickup_address: customerDetail.pickupAddress,
      pickupLocations: customerDetail.pickupLocations.map((loc) => ({
        label: loc.label,
        link: loc.link,
      })),

      // ── Preferences payload — BE expects `cust_preferences` ──────────────
      cust_preferences: customerDetail.cust_preferences.map((p) => ({
        id: p.id ?? null,
        name: p.name,
        store_id: userStoreID,
      })),

      company_name: customerDetail.businessInfo.companyName || null,
      company_email: customerDetail.businessInfo.companyEmail || null,
      company_gst: customerDetail.businessInfo.gstNumber || null,

      documents: customerDetail.documents.map((doc) => ({
        doc_id: doc.doc_id,
        name: doc.name,
        number: doc.number,
        file: doc.file,
        fileName: doc.fileName,
        renewalDate: doc.renewalDate || null,
      })),

      cust_type: customerDetail.cust_type || null,
      pickup_place: customerDetail.pickupPlace || null,
    };

    try {
      setIsSaving(true);
      let customerAPIResponse;

      if (selectedCustomerId && !isNewCustomer) {
        customerAPIResponse = await axios.put(
          `${API_BASE_URL}/api/travel/customers/${selectedCustomerId}`,
          payload,
        );
      } else {
        customerAPIResponse = await axios.post(
          `${API_BASE_URL}/api/travel/customers/create`,
          payload,
        );
      }

      if (!customerAPIResponse.data.success) throw customerAPIResponse.data;

      setSnackBarConfig({
        open: true,
        message:
          customerAPIResponse.data.statusMessage ||
          "Customer saved successfully",
        severity: "success",
      });

      if (isCreateBooking) {
        navigate("/travel/booking", {
          state: { customer_data: customerAPIResponse?.data?.data },
        });
      }

      refreshCustomers(true);
      await placesFetcher();

      setTimeout(() => {
        setCustomerDetail(initialCustomerState);
        setSelectedCustomerId(null);
        setIsNewCustomer(true);
        setShowCompanyDetails(false);
        setDuplicateFields({
          mobile: null,
          waNumber: null,
          relatedMobiles: {},
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
        setFieldErrors({ name: false, mobile: false, pickupAddress: false });
      }, 300);
    } catch (error) {
      console.error("❌ API Error:", error);
      setSnackBarConfig({
        open: true,
        message: error?.statusMessage || "Operation failed",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Filtered preferences list based on search input
  const filteredPreferences = allPreferences.filter((pref) =>
    pref.name.toLowerCase().includes(preferenceSearch.toLowerCase()),
  );

  const canCreateNewPreference =
    preferenceSearch.trim().length > 0 &&
    !allPreferences.some(
      (p) => p.name.toLowerCase() === preferenceSearch.trim().toLowerCase(),
    );

  // Count active "more" filters
  const moreFiltersActiveCount = [
    selectedPreferenceFilter,
    selectedCategoryFilter,
    selectedStageFilter,
  ].filter(Boolean).length;

  return (
    <Box ref={topRef} sx={{ backgroundColor: BG, minHeight: "100vh", p: 2 }}>
      {/* Loading bar */}
      {isSaving && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            zIndex: 9999,
            background: `linear-gradient(90deg, ${PRIMARY}, #a78bfa)`,
            animation: "loadingBar 1.2s ease-in-out infinite",
            "@keyframes loadingBar": {
              "0%": {
                opacity: 0.6,
                transform: "scaleX(0.3)",
                transformOrigin: "left",
              },
              "50%": {
                opacity: 1,
                transform: "scaleX(0.8)",
                transformOrigin: "left",
              },
              "100%": {
                opacity: 0.6,
                transform: "scaleX(1)",
                transformOrigin: "left",
              },
            },
          }}
        />
      )}

      {/* Mobile Header — visible only on mobile */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          gap: 1.5,
          mb: 2,
          pb: 1.5,
          borderBottom: `1px solid ${PRIMARY}20`,
        }}
      >
        <PersonRoundedIcon sx={{ fill: PRIMARY }} />
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ color: PRIMARY }}
        >
          Customer Master
        </Typography>
      </Box>

      {/* Main Content */}
      <Grid container spacing={2}>
        {/* ================= LEFT SIDEBAR ================= */}
        <Grid
          item
          xs={12}
          md={isEditMode ? 3 : 4}
          sx={{ order: { xs: 2, md: 1 } }}
        >
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
            {/* Gradient header */}
            <Box
              sx={{
                background:
                  "linear-gradient(90deg, #1aa7a1 0%, #2f6fb3 50%, #4b2ca3 100%)",
                p: 2,
              }}
            >
              {/* Header for Desktop only */}
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center", // ← was "flex-start"
                  justifyContent: "flex-start",
                  gap: 1.5,
                  mb: 1,
                }}
              >
                <Box
                  onClick={() => navigate(-1)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    borderRadius: 2,
                    bgcolor: "#03b9d1",
                    p: 0.5,
                  }}
                >
                  <ArrowBackRoundedIcon sx={{ fill: "#FFF" }} />
                </Box>
                <Box
                  onClick={() => navigate("/travel/dashboard")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    borderRadius: 2,
                    bgcolor: "#03b9d1",
                    p: 0.5,
                  }}
                >
                  <HouseIcon sx={{ fill: "#FFF" }} />
                </Box>
                <PersonRoundedIcon sx={{ fill: "#FFF" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ color: "#FFF" }} // ← removed gutterBottom, mb, textAlign
                >
                  Customer Master
                </Typography>
              </Box>

              {/* Search */}
              {/* Search */}
              <Box sx={{ mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name, code, mobile, place..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "rgba(255,255,255,0.7)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 0.75,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      color: "#fff",
                      background: "rgba(255, 255, 255, 0.15)",
                      backdropFilter: "blur(12px)",
                      WebkitBackdropFilter: "blur(12px)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      transition: "all 0.3s ease",
                      "& fieldset": { border: "none" },
                      "&:hover": { background: "rgba(255, 255, 255, 0.22)" },
                      "&.Mui-focused": {
                        background: "rgba(255, 255, 255, 0.25)",
                        boxShadow: "0 0 0 2px rgba(255,255,255,0.25)",
                      },
                    },
                    "& input::placeholder": {
                      color: "rgba(255,255,255,0.75)",
                      opacity: 1,
                    },
                  }}
                />
                {/* Search mode chips */}
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  {[
                    {
                      label: "Start",
                      value: "start",
                      title: "Match from beginning",
                    },
                    { label: "Wild", value: "wild", title: "Match anywhere" },
                    { label: "End", value: "end", title: "Match from end" },
                  ].map((mode) => (
                    <Chip
                      key={mode.value}
                      label={mode.label}
                      size="small"
                      clickable
                      onClick={() => setSearchMode(mode.value)}
                      sx={glassChipSx(searchMode === mode.value)}
                    />
                  ))}
                </Box>
              </Box>

              {/* Place filter */}
              {allPlaces.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <FilterLabel>Place</FilterLabel>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    <Chip
                      label="All"
                      size="small"
                      clickable
                      onClick={() => setSelectedPlaceFilter(null)}
                      sx={glassChipSx(!selectedPlaceFilter)}
                    />
                    {allPlaces.map((place, idx) => (
                      <Chip
                        key={idx}
                        label={place}
                        size="small"
                        clickable
                        onClick={() =>
                          setSelectedPlaceFilter(
                            selectedPlaceFilter === place ? null : place,
                          )
                        }
                        sx={glassChipSx(selectedPlaceFilter === place)}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Customer Type filter */}
              {allCustomerTypes.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <FilterLabel>Customer Type</FilterLabel>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    <Chip
                      label="All"
                      size="small"
                      clickable
                      onClick={() => setSelectedTypeFilter(null)}
                      sx={glassChipSx(!selectedTypeFilter)}
                    />
                    {allCustomerTypes.map((type) => (
                      <Chip
                        key={type.id}
                        label={type.name}
                        size="small"
                        clickable
                        onClick={() =>
                          setSelectedTypeFilter(
                            selectedTypeFilter === type.name ? null : type.name,
                          )
                        }
                        sx={glassChipSx(selectedTypeFilter === type.name)}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Sort */}
              <Box sx={{ mb: 1 }}>
                <FilterLabel>Sort</FilterLabel>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {SORT_OPTIONS.map((opt) => (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      size="small"
                      clickable
                      onClick={() => setSortOrder(opt.value)}
                      sx={glassChipSx(sortOrder === opt.value)}
                    />
                  ))}
                </Box>
              </Box>

              {/* More filters toggle */}
              <Box
                onClick={() => setShowMoreFilters((prev) => !prev)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  mb: showMoreFilters ? 1 : 0,
                  width: "fit-content",
                }}
              >
                <ExpandMoreRoundedIcon
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: 18,
                    transform: showMoreFilters
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.25s ease",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      moreFiltersActiveCount > 0
                        ? "#fff"
                        : "rgba(255,255,255,0.85)",
                    fontWeight: moreFiltersActiveCount > 0 ? 700 : 500,
                    fontSize: "0.78rem",
                  }}
                >
                  {showMoreFilters ? "Less filters" : "More filters"}
                  {moreFiltersActiveCount > 0 &&
                    ` (${moreFiltersActiveCount} active)`}
                </Typography>
              </Box>

              {/* Collapsible: Preference + Category + Stage */}
              <Collapse in={showMoreFilters} timeout="auto">
                <Box sx={{ pt: 1 }}>
                  {/* Preference filter */}
                  {allPreferences.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <FilterLabel>Preference</FilterLabel>
                      <Box
                        sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}
                      >
                        <Chip
                          label="All"
                          size="small"
                          clickable
                          onClick={() => setSelectedPreferenceFilter(null)}
                          sx={glassChipSx(!selectedPreferenceFilter)}
                        />
                        {allPreferences.map((pref, idx) => (
                          <Chip
                            key={idx}
                            label={pref.name}
                            size="small"
                            clickable
                            onClick={() =>
                              setSelectedPreferenceFilter(
                                selectedPreferenceFilter === pref.name
                                  ? null
                                  : pref.name,
                              )
                            }
                            sx={glassChipSx(
                              selectedPreferenceFilter === pref.name,
                            )}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Customer Category filter */}
                  <Box sx={{ mb: 1 }}>
                    <FilterLabel>Customer Category</FilterLabel>
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                      <Chip
                        label="All"
                        size="small"
                        clickable
                        onClick={() => setSelectedCategoryFilter(null)}
                        sx={glassChipSx(!selectedCategoryFilter)}
                      />
                      {CATEGORY_OPTIONS.map((cat) => (
                        <Chip
                          key={cat}
                          label={cat}
                          size="small"
                          clickable
                          onClick={() =>
                            setSelectedCategoryFilter(
                              selectedCategoryFilter === cat ? null : cat,
                            )
                          }
                          sx={glassChipSx(selectedCategoryFilter === cat)}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Customer Stage filter */}
                  <Box sx={{ mb: 0.5 }}>
                    <FilterLabel>Customer Stage</FilterLabel>
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                      <Chip
                        label="All"
                        size="small"
                        clickable
                        onClick={() => setSelectedStageFilter(null)}
                        sx={glassChipSx(!selectedStageFilter)}
                      />
                      {STAGE_OPTIONS.map((stage) => (
                        <Chip
                          key={stage}
                          label={stage}
                          size="small"
                          clickable
                          onClick={() =>
                            setSelectedStageFilter(
                              selectedStageFilter === stage ? null : stage,
                            )
                          }
                          sx={glassChipSx(selectedStageFilter === stage)}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Collapse>
            </Box>

            {/* Add Customer Button */}
            <Box sx={{ px: 5, mb: 2, mt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<PersonAddRoundedIcon />}
                onClick={handleAddNewCustomer}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1,
                  borderRadius: "12px",
                  backgroundColor: PRIMARY,
                  boxShadow:
                    "0px 6px 18px rgba(0,0,0,0.18), 0px 2px 6px rgba(0,0,0,0.12)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    backgroundColor: PRIMARY,
                    transform: "translateY(-2px)",
                    boxShadow:
                      "0px 10px 24px rgba(0,0,0,0.22), 0px 4px 10px rgba(0,0,0,0.16)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.18)",
                  },
                }}
              >
                New Customer
              </Button>
            </Box>

            <Box sx={{ width: "100%", height: 1.5, bgcolor: "#d6d6d6" }} />

            {/* Customer count */}
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {isListLoading
                  ? "Loading..."
                  : `${totalCustomers} customer${
                      totalCustomers !== 1 ? "s" : ""
                    } found`}
              </Typography>
            </Box>

            {/* Customers list with infinite scroll */}
            <List
              ref={listRef}
              onScroll={handleScroll}
              sx={{
                maxHeight: "calc(100vh - 250px)",
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 0, height: 0 },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                p: 2,
              }}
            >
              {customers?.map((customer, idx) => (
                <React.Fragment key={customer.customer_id}>
                  <ListItem
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      pr: 0.5,
                      cursor: "pointer",
                      backgroundColor:
                        selectedCustomerId === customer.customer_id
                          ? `${PRIMARY}10`
                          : "transparent",
                    }}
                    onClick={() => fetchOneCustomer(customer.customer_id)}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{ backgroundColor: PRIMARY, width: 36, height: 36 }}
                      >
                        {customer.name[0]}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600}>
                          {customer.name}
                        </Typography>
                      }
                      secondary={
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {customer.mobile}
                            </Typography>
                            {customer.custType && (
                              <Chip
                                label={customer.custType}
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: "0.6rem",
                                  backgroundColor: `${PRIMARY}15`,
                                  color: PRIMARY,
                                }}
                              />
                            )}
                          </Stack>
                          {customer.pickupPlace && (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              📍 {customer.pickupPlace}
                            </Typography>
                          )}
                          {customer.address && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {customer.address}
                            </Typography>
                          )}
                        </Stack>
                      }
                    />

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCustomer(customer);
                        setOpenDialog(true);
                      }}
                      sx={{ ml: 1, color: PRIMARY }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                  {idx < customers.length - 1 && <Divider />}
                </React.Fragment>
              ))}

              {isLoadingMore && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!hasMore && customers.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  sx={{ display: "block", py: 2 }}
                >
                  No more customers
                </Typography>
              )}

              {!isListLoading && customers.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ py: 3 }}
                >
                  No customers found
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        {/* ================= CENTER CONTENT ================= */}
        <Grid
          item
          xs={12}
          md={isEditMode ? 6 : 8}
          sx={{ order: { xs: 1, md: 2 } }}
        >
          <Stack spacing={2}>
            {/* CUSTOMER DETAILS */}
            <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
              {/* Heading & Save Button */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography
                  variant={isMobile ? "subtitle1" : "h4"}
                  fontWeight={600}
                  gutterBottom
                  sx={{ color: PRIMARY, mb: 1.5 }}
                >
                  Customer Details{" "}
                  <span
                    style={{
                      fontWeight: 400,
                      color: "#c5c5c5ff",
                      fontSize: 14,
                    }}
                  >
                    *required
                  </span>
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  size={isMobile ? "small" : "large"}
                  // disabled={!validateDetails() || isSaving || hasAnyDuplicate}
                  sx={{
                    backgroundColor: PRIMARY,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {/* {isNewCustomer ? "Save Customer" : "Save Changes"} */}
                  Save
                </Button>
              </Box>

              <Stack spacing={1.5}>
                {/* Party Code & Customer Type */}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  sx={{ width: "100%" }}
                >
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Party Code"
                      value={customerDetail.partyCode || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleChange("partyCode", value);
                        checkPartyCodeDuplicate(value);
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Tab" &&
                          partyCodeDuplicate &&
                          partyCodeDuplicate.customer_id !== selectedCustomerId
                        ) {
                          e.preventDefault();
                          e.stopPropagation();
                          fetchOneCustomer(partyCodeDuplicate.customer_id);
                        }
                      }}
                      error={
                        !!partyCodeDuplicate &&
                        partyCodeDuplicate.customer_id !== selectedCustomerId
                      }
                      helperText={
                        !!partyCodeDuplicate &&
                        partyCodeDuplicate.customer_id !== selectedCustomerId
                          ? `⚠️ Party code "${customerDetail.partyCode}" belongs to "${partyCodeDuplicate.name}"`
                          : ""
                      }
                    />
                  </Box>

                  <Box flex={1}>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Customer Type"
                      InputLabelProps={{ shrink: true }}
                      value={customerDetail.cust_type || ""}
                      onChange={async (e) => {
                        const val = e.target.value;
                        if (val === "__create__") {
                          const newName = customerTypeSearch.trim();
                          if (!newName) return;
                          try {
                            const res = await axios.post(
                              `${API_BASE_URL}/api/travel/customers/customer-types/create`,
                              { store_id: userStoreID, name: newName },
                            );
                            if (
                              res.data.success ||
                              res.data.statusCode === 409
                            ) {
                              const created = res.data.data;
                              setAllCustomerTypes((prev) =>
                                prev.some((t) => t.id === created.id)
                                  ? prev
                                  : [...prev, created],
                              );
                              handleChange("cust_type", created.name);
                            }
                          } catch {
                            console.error("Failed to create type");
                          }
                          setCustomerTypeSearch("");
                        } else {
                          handleChange("cust_type", val);
                        }
                      }}
                      SelectProps={{
                        displayEmpty: true,
                        MenuProps: { PaperProps: { sx: { maxHeight: 260 } } },
                      }}
                    >
                      <Box
                        onKeyDown={(e) => e.stopPropagation()}
                        sx={{
                          px: 1.5,
                          py: 1,
                          position: "sticky",
                          top: 0,
                          bgcolor: "background.paper",
                          zIndex: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Search or type new..."
                          value={customerTypeSearch}
                          onChange={(e) =>
                            setCustomerTypeSearch(e.target.value)
                          }
                          autoFocus
                        />
                      </Box>

                      <MenuItem value="">
                        <em style={{ color: "#aaa" }}>None</em>
                      </MenuItem>

                      {allCustomerTypes
                        .filter((t) =>
                          t.name
                            .toLowerCase()
                            .includes(customerTypeSearch.toLowerCase()),
                        )
                        .map((t) => (
                          <MenuItem key={t.id} value={t.name}>
                            {t.name}
                          </MenuItem>
                        ))}

                      {customerTypeSearch.trim().length > 0 &&
                        !allCustomerTypes.some(
                          (t) =>
                            t.name.toLowerCase() ===
                            customerTypeSearch.trim().toLowerCase(),
                        ) && (
                          <MenuItem
                            value="__create__"
                            sx={{ color: "success.main", fontWeight: 600 }}
                          >
                            + Create "{customerTypeSearch.trim()}"
                          </MenuItem>
                        )}
                    </TextField>
                  </Box>
                </Stack>

                {/* Name & Mobile */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Box flex={1}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      label="Name"
                      value={customerDetail.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      error={fieldErrors.name}
                      helperText={fieldErrors.name ? "Name is required" : ""}
                    />
                  </Box>

                  <Box flex={1}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      label="Mobile"
                      placeholder="Enter number"
                      value={customerDetail.mobile}
                      onChange={(e) => {
                        setIsWaManuallyEdited(false);
                        handleChange("mobile", e.target.value);
                        checkNumberDuplicate("mobile", e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Tab") {
                          // Don't prevent default — let focus move naturally
                          // But also trigger the lookup
                          handleFetchByMobile(customerDetail.mobile);
                        }
                      }}
                      error={!!duplicateFields.mobile || fieldErrors.mobile}
                      helperText={
                        duplicateFields.mobile
                          ? `⚠️ Belongs to: ${duplicateFields.mobile.name}`
                          : fieldErrors.mobile
                          ? "Mobile is required"
                          : "Country codes are allowed (e.g. +91, +1) · Press Enter to lookup"
                      }
                    />
                  </Box>
                </Stack>

                {/* Additional Contacts Heading & Button */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    Additional Contacts
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",

                      px: 0.75,
                      py: 0.25,
                      borderRadius: 2,
                      bgcolor: PRIMARY,
                      cursor: "pointer",
                    }}
                  >
                    <AddIcon sx={{ fill: "#FFF", fontWeight: 600 }} />
                    <Typography
                      onClick={addRelatedMobile}
                      sx={{ color: "#FFF", fontWeight: 500 }}
                    >
                      ADD
                    </Typography>
                  </Box>
                </Box>

                {/* Additional Related Mobile Numbers */}
                {customerDetail.relatedMobiles.length > 0 &&
                  (isMobile ? (
                    <Stack spacing={1.5}>
                      {customerDetail.relatedMobiles.map((relMob, i) => (
                        <Paper
                          key={i}
                          variant="outlined"
                          sx={{ p: 1.5, borderRadius: 2 }}
                        >
                          <Stack spacing={1}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <TextField
                                size="small"
                                // fullWidth
                                value={relMob.relation}
                                onChange={(e) =>
                                  updateRelatedMobile(
                                    i,
                                    "relation",
                                    e.target.value,
                                  )
                                }
                                placeholder="Relation"
                              />

                              <TextField
                                size="small"
                                fullWidth
                                value={relMob.number}
                                onChange={(e) =>
                                  updateRelatedMobile(
                                    i,
                                    "number",
                                    e.target.value,
                                  )
                                }
                                placeholder="Number"
                                error={!!duplicateFields.relatedMobiles[i]}
                                helperText={
                                  duplicateFields.relatedMobiles[i]
                                    ? `⚠️ Belongs to: ${duplicateFields.relatedMobiles[i].name}`
                                    : ""
                                }
                              />

                              <DeleteOutlineIcon
                                onClick={() => deleteRelatedMobile(i)}
                                sx={{
                                  fontSize: 28,
                                  fill: "rgb(201, 0, 0)",
                                  cursor: "pointer",
                                }}
                              />
                            </Box>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ overflowX: "auto" }}>
                      <Table
                        size="small"
                        sx={{ width: "100%", tableLayout: "fixed" }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "45%" }}>
                              Relation *
                            </TableCell>
                            <TableCell sx={{ width: "45%" }}>
                              Number *{" "}
                              <span style={{ fontSize: 12, color: "#A2A2A2" }}>
                                Country codes are allowed (e.g. +91, +1)
                              </span>
                            </TableCell>
                            <TableCell sx={{ width: "10%" }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customerDetail.relatedMobiles.map((relMob, i) => (
                            <TableRow key={i} sx={{ verticalAlign: "top" }}>
                              <TableCell sx={{ py: 0.5, pr: 1 }}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={relMob.relation}
                                  onChange={(e) =>
                                    updateRelatedMobile(
                                      i,
                                      "relation",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Relation"
                                />
                              </TableCell>
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={relMob.number}
                                  onChange={(e) =>
                                    updateRelatedMobile(
                                      i,
                                      "number",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Number"
                                  error={!!duplicateFields.relatedMobiles[i]}
                                  helperText={
                                    duplicateFields.relatedMobiles[i]
                                      ? `⚠️ Belongs to: ${duplicateFields.relatedMobiles[i].name}`
                                      : ""
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ width: "10%" }}>
                                <DeleteOutlineIcon
                                  onClick={() => deleteRelatedMobile(i)}
                                  sx={{
                                    fontSize: 28,
                                    fill: "rgb(201, 0, 0)",
                                    mt: 0.5,
                                    cursor: "pointer",
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  ))}

                {/* Pickup Details */}
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      required
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      label="Address"
                      value={customerDetail.pickupAddress}
                      onChange={(e) =>
                        handleChange("pickupAddress", e.target.value)
                      }
                      error={fieldErrors.pickupAddress}
                      helperText={
                        fieldErrors.pickupAddress ? "Address is required" : ""
                      }
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Pick-up Place"
                      placeholder="e.g. Mumbai, Andheri West"
                      value={customerDetail.pickupPlace || ""}
                      onChange={(e) =>
                        handleChange("pickupPlace", e.target.value)
                      }
                    />
                  </Stack>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addPickupLocation}
                    sx={{ alignSelf: "flex-start", textTransform: "none" }}
                  >
                    Add pickup location
                  </Button>

                  {customerDetail.pickupLocations.length > 0 &&
                    (isMobile ? (
                      <Stack spacing={1.5}>
                        {customerDetail.pickupLocations.map((loc, i) => (
                          <Paper
                            key={i}
                            variant="outlined"
                            sx={{ p: 1.5, borderRadius: 2 }}
                          >
                            <Stack spacing={1}>
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Home / Office"
                                value={loc.label}
                                onChange={(e) =>
                                  updatePickupLocation(
                                    i,
                                    "label",
                                    e.target.value,
                                  )
                                }
                              />
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Google Maps / WhatsApp location link"
                                value={loc.link}
                                onChange={(e) =>
                                  updatePickupLocation(
                                    i,
                                    "link",
                                    e.target.value,
                                  )
                                }
                              />
                              {loc.link && (
                                <Typography variant="caption">
                                  <a
                                    href={loc.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: PRIMARY,
                                      fontStyle: "italic",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Open in Google Maps
                                  </a>
                                </Typography>
                              )}
                              <Box display="flex" justifyContent="flex-end">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => deletePickupLocation(i)}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Box>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <Box sx={{ overflowX: "auto" }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: "30%" }}>
                                Location Label *
                              </TableCell>
                              <TableCell sx={{ width: "65%" }}>
                                Location Link *
                              </TableCell>
                              <TableCell sx={{ width: "5%" }} />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customerDetail.pickupLocations.map((loc, i) => (
                              <TableRow key={i}>
                                <TableCell sx={{ verticalAlign: "top" }}>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Home / Office"
                                    value={loc.label}
                                    onChange={(e) =>
                                      updatePickupLocation(
                                        i,
                                        "label",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell sx={{ verticalAlign: "top" }}>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Google Maps / WhatsApp location link"
                                    value={loc.link}
                                    onChange={(e) =>
                                      updatePickupLocation(
                                        i,
                                        "link",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  {loc.link && (
                                    <Typography
                                      variant="caption"
                                      sx={{ mt: 0.5, display: "block" }}
                                    >
                                      <a
                                        href={loc.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color: PRIMARY,
                                          fontStyle: "italic",
                                          fontWeight: 500,
                                        }}
                                      >
                                        Open in Google Maps
                                      </a>
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ verticalAlign: "top" }}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => deletePickupLocation(i)}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    ))}
                </Stack>

                {/* Category + Stage + Preferences */}
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems="flex-start"
                >
                  {/* Customer Category */}
                  <Box sx={{ minWidth: 0, flex: { md: 3 } }}>
                    <Stack spacing={0.5}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Customer Category
                      </Typography>
                      <Box
                        sx={{
                          minHeight: 40,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {["A", "B", "C", "D"].map((type) => (
                            <Chip
                              key={type}
                              label={type}
                              size="small"
                              clickable
                              color={
                                customerDetail.customerType === type
                                  ? "primary"
                                  : "default"
                              }
                              onClick={() => toggleChip("customerType", type)}
                              sx={{
                                ...(customerDetail.customerType === type && {
                                  backgroundColor: PRIMARY,
                                }),
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>

                  {/* Customer Stage */}
                  <Box
                    sx={{
                      minWidth: 0,
                      width: { xs: "100%", md: "auto" },
                      flex: { md: 3 },
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Customer Stage
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={customerDetail.stage}
                        onChange={(e) => handleChange("stage", e.target.value)}
                      >
                        {STAGE_OPTIONS.map((stage) => (
                          <MenuItem key={stage} value={stage}>
                            {stage}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Box>

                  {/* Preferences */}
                  <Box sx={{ minWidth: 0, flex: { md: 6 } }}>
                    <Stack spacing={0.5}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Preferences
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search or create preferences..."
                        value={preferenceSearch}
                        onChange={(e) => setPreferenceSearch(e.target.value)}
                      />
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {filteredPreferences.map((pref) => {
                          const isSelected =
                            customerDetail.cust_preferences.some(
                              (p) =>
                                (p.id && p.id === pref.id) ||
                                p.name.toLowerCase() ===
                                  pref.name.toLowerCase(),
                            );
                          return (
                            <Chip
                              key={`${pref.id}-${pref.name}`}
                              label={pref.name}
                              size="small"
                              clickable
                              color={isSelected ? "primary" : "default"}
                              onClick={() => togglePreference(pref)}
                              sx={{
                                ...(isSelected && { backgroundColor: PRIMARY }),
                              }}
                            />
                          );
                        })}
                        {canCreateNewPreference && (
                          <Chip
                            label={`Create "${preferenceSearch}"`}
                            size="small"
                            clickable
                            color="success"
                            onClick={createAndSelectPreference}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>

                {/* Email, DOB & Note */}
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  sx={{ pt: 1.5 }}
                  alignItems="flex-start"
                >
                  {/* Email */}
                  <Box
                    sx={{
                      minWidth: 0,
                      width: { xs: "100%", md: "auto" },
                      flex: { md: 2 },
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="E-mail"
                      type="email"
                      value={customerDetail.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                    />
                  </Box>

                  {/* DOB */}
                  <Box
                    sx={{
                      minWidth: 0,
                      width: { xs: "100%", md: "auto" },
                      flex: { md: 1 },
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Date of Birth"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={customerDetail.dob}
                      onChange={(e) => handleChange("dob", e.target.value)}
                    />
                  </Box>

                  {/* Note */}
                  <Box
                    sx={{
                      minWidth: 0,
                      width: { xs: "100%", md: "auto" },
                      flex: { md: 3 },
                    }}
                  >
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      label="Note"
                      value={customerDetail.note}
                      onChange={(e) => handleChange("note", e.target.value)}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            {/* COMPANY DETAILS */}
            <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ color: PRIMARY }}
                >
                  Company Details
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowCompanyDetails((prev) => !prev)}
                  sx={{
                    transform: showCompanyDetails
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "0.3s",
                  }}
                >
                  <ExpandMoreRoundedIcon />
                </IconButton>
              </Box>

              <Collapse in={showCompanyDetails} timeout="auto" unmountOnExit>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Company Name"
                    value={customerDetail.businessInfo.companyName}
                    onChange={(e) =>
                      handleBusinessChange("companyName", e.target.value)
                    }
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Company Email"
                    type="email"
                    value={customerDetail.businessInfo.companyEmail}
                    onChange={(e) =>
                      handleBusinessChange("companyEmail", e.target.value)
                    }
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="GST Number"
                    value={customerDetail.businessInfo.gstNumber}
                    onChange={(e) =>
                      handleBusinessChange("gstNumber", e.target.value)
                    }
                  />
                </Stack>
              </Collapse>
            </Paper>

            {/* DOCUMENTS */}
            <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: PRIMARY, mb: 1.5 }}
              >
                Documents{" "}
                <span
                  style={{ fontWeight: 400, color: "#c5c5c5ff", fontSize: 14 }}
                >
                  *required
                </span>
              </Typography>

              {customerDetail.documents.length > 0 &&
                (isMobile ? (
                  <Stack spacing={1.5}>
                    {customerDetail.documents.map((doc, i) => (
                      <Paper
                        key={i}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 2 }}
                      >
                        <Stack spacing={1}>
                          <TextField
                            size="small"
                            fullWidth
                            value={doc.name}
                            onChange={(e) =>
                              updateDocument(i, "name", e.target.value)
                            }
                            placeholder="Document name"
                          />
                          <TextField
                            size="small"
                            fullWidth
                            value={doc.number}
                            onChange={(e) =>
                              updateDocument(i, "number", e.target.value)
                            }
                            placeholder="Number"
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            component="label"
                            fullWidth
                            sx={{
                              textTransform: "none",
                              justifyContent: "flex-start",
                            }}
                          >
                            {doc.fileName
                              ? doc.fileName.length > 35
                                ? doc.fileName.substring(0, 35) + "..."
                                : doc.fileName
                              : "Upload document file"}
                            <input
                              type="file"
                              hidden
                              onChange={(e) => handleFileUpload(i, e)}
                            />
                          </Button>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              // gap: 1,
                            }}
                          >
                            <TextField
                              size="small"
                              type="date"
                              value={doc.renewalDate}
                              onChange={(e) =>
                                updateDocument(i, "renewalDate", e.target.value)
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                            <Tooltip title="View document">
                              <IconButton
                                size="small"
                                onClick={() => setViewerDoc(doc)}
                                sx={{ color: PRIMARY }}
                              >
                                <VisibilityRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteDocument(i)}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 1 }}>Name *</TableCell>
                          <TableCell sx={{ py: 1 }}>Number *</TableCell>
                          <TableCell sx={{ py: 1 }}>Upload</TableCell>
                          <TableCell sx={{ py: 1 }}>Renewal</TableCell>
                          <TableCell sx={{ py: 1 }}>View</TableCell>
                          <TableCell sx={{ py: 1 }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customerDetail.documents.map((doc, i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                value={doc.name}
                                onChange={(e) =>
                                  updateDocument(i, "name", e.target.value)
                                }
                                placeholder="Document name"
                                sx={{ minWidth: 120 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                value={doc.number}
                                onChange={(e) =>
                                  updateDocument(i, "number", e.target.value)
                                }
                                placeholder="Number"
                                sx={{ minWidth: 100 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                component="label"
                                sx={{ textTransform: "none" }}
                              >
                                {doc.fileName
                                  ? doc.fileName.substring(0, 15) + "..."
                                  : "Upload"}
                                <input
                                  type="file"
                                  hidden
                                  onChange={(e) => handleFileUpload(i, e)}
                                />
                              </Button>
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                type="date"
                                value={doc.renewalDate}
                                onChange={(e) =>
                                  updateDocument(
                                    i,
                                    "renewalDate",
                                    e.target.value,
                                  )
                                }
                                InputLabelProps={{ shrink: true }}
                                sx={{ minWidth: 130 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <Tooltip title="View document">
                                <IconButton
                                  size="small"
                                  onClick={() => setViewerDoc(doc)}
                                  sx={{ color: PRIMARY }}
                                >
                                  <VisibilityRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteDocument(i)}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ))}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addDocument}
                size="small"
                sx={{ mt: 1.5, textTransform: "none" }}
              >
                Add Document
              </Button>
            </Paper>

            {/* CANCEL BUTTON */}
            <Stack direction="row" spacing={1.5} justifyContent="space-between">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                size="small"
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Grid>

        {/* ================= RIGHT SIDEBAR ================= */}
        <Grid
          item
          xs={12}
          md={isEditMode ? 3 : 0}
          sx={{ order: { xs: 3, md: 3 }, overflow: "hidden" }}
        >
          <Slide
            direction="left"
            in={isEditMode}
            mountOnEnter
            unmountOnExit
            timeout={350}
          >
            <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{ color: PRIMARY, mb: 1.5 }}
              >
                Past Trips
              </Typography>
              <List
                sx={{
                  maxHeight: "calc(100vh - 160px)",
                  overflow: "auto",
                  "&::-webkit-scrollbar": { width: 0, height: 0 },
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {customerDetail.trips.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      No past trips found
                    </Typography>
                  </Box>
                ) : (
                  customerDetail.trips.map((trip, idx) => (
                    <PastTripCard
                      key={trip.bookingId || idx}
                      trip={trip}
                      index={idx}
                    />
                  ))
                )}
              </List>
            </Paper>
          </Slide>
        </Grid>
      </Grid>

      {/* Document Viewer */}
      <DocumentViewerDialog
        doc={viewerDoc}
        onClose={() => setViewerDoc(null)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete customer "{selectedCustomer?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteCustomer(selectedCustomer?.customer_id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ "&.MuiSnackbar-root": { bottom: "56px !important" } }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerMaster;
